import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { saveSessionLearning, getSessionLearning, markSessionLevelResult, getKnowledgeChunks, getRules, getRiskSettings, saveKnowledgeChunk, saveRule, clearLegacyTradingMemory } from '../../services/storage';
import { speak, speakAlert, startListening, stopListening, detectSaveCommand, isMuted, toggleMute } from '../../services/voiceService';
import { useJarvisContext, A } from '../../context/JarvisContext';
import { autoLearnFromAnalysis } from '../../services/jarvisAutoLearn';
import { THEME } from '../../styles/theme';
import { getCandles as getLiveCandles } from '../../a-agents/trading/A_LivePrice';
import { run as runSignal } from '../../a-agents/trading/A_Signal';
import { run as runReversal } from '../../a-agents/trading/A_Reversal';
import { run as runRisk } from '../../a-agents/trading/A_Risk';
import { run as runBacktest, saveBacktestResult, compareToLastBacktest, isTimeframeApprovedForLive, sweepADXThreshold } from '../../a-agents/trading/A_Backtest';
import { apiUrl } from '../../services/apiBase';
import { calcHtfBias, autoLogSignalAsTrade, autoLogReversalAsTrade, autoLogStructureToICT, autoLogOrderBlocksToICT, saveSignalSnapshot } from '../../services/scriptAnalysis';
import { MemoryManager } from '../../brain/MemoryManager';
import { NS } from '../../brain/AgentRegistry';

// calcHtfBias (EMA50/EMA200, confirmed-bar-only) now lives in
// scriptAnalysis.js — shared with the chat's multi-timeframe analysis
// instead of two copies drifting apart.

// Auto HTF map — matches jarvisDNA.trading.validated_strategy.htf_confirmation
// (extends scriptAnalysis.js's HTF_MAP with 1m/3m/5m — those stay gated
// here, scriptAnalysis.js only ever deals with the always-validated TFs)
const HTF_MAP = { '1m': '15m', '3m': '15m', '5m': '15m', '15m': '4h', '30m': '4h', '45m': '4h', '1h': '4h', '4h': '1d' };

// 1m/3m/5m are "gated" — Pine backtests on them came back weak/negative
// (3m: -0.35R, 5m: +0.06R barely breakeven, 1m untested). Instead of hard
// removing them, Jarvis unlocks each one automatically the moment ITS OWN
// saved backtest shows a real edge — see isTimeframeApprovedForLive() in
// A_Backtest.js. This is the "भविष्यात backtest result बघून trade करता
// येईल" mechanism Sir asked for.
const GATED_TFS = ['1m', '3m', '5m'];

// Map A-Signal's honest output into the shape the existing UI already
// renders (trend/bias/trade_plan/grade/etc.) — no fabricated confidence
// score: grade is derived from how many of the 5 real gates passed, not a
// guessed number.
function adaptSignalToLegacyShape(signal, timeframe, riskResult) {
  const gatesTotal = 5; // Regime, HTF, OTE zone, confirmation candle, R:R
  let gatesPassed = 0;
  if (signal.regime?.isTrending) gatesPassed++;
  if (signal.structure?.valid) gatesPassed++;
  if (signal.fib?.inZone) gatesPassed++;
  if (signal.action !== 'WAIT') gatesPassed += 2; // confirmation candle + R:R both implied by a firm action
  const score = Math.round((gatesPassed / gatesTotal) * 100);
  const grade = score >= 100 ? 'A+' : score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : 'D';

  return {
    symbol: 'XAUUSD',
    timeframe,
    price: signal.entry || signal.structure?.currentPrice || null,
    trend: signal.structure?.trend || 'SIDEWAYS',
    structure: signal.structure?.valid ? (signal.action !== 'WAIT' ? 'BOS confirmed' : 'Structure OK, gate pending') : 'Undetermined',
    bias: signal.action === 'BUY' ? 'BUY' : signal.action === 'SELL' ? 'SELL' : 'NEUTRAL',
    setup_score: score,
    grade,
    trade_plan: signal.action !== 'WAIT' ? {
      action: signal.action,
      entry:  signal.entry,
      sl:     signal.sl,
      tp1:    signal.target,
      tp2:    null,
      rr:     `1:${signal.rr}`,
      lot_size:  riskResult?.lotSize  ?? null,
      risk_usd:  riskResult?.actualRisk ?? null,
      reason: signal.reason,
    } : { action: 'WAIT', reason: signal.reason },
    key_observation: signal.reason,
    rules_applied: signal.action !== 'WAIT'
      ? ['A-Structure (swing BOS/CHoCH)', 'A-Fibonacci (OTE 0.618-0.79)', 'A-Regime (ADX>20)', 'Confirmation candle', `Min R:R ${signal.rr}`]
      : [],
    alert: null,
    liquidity: 'None visible',
    drawn_levels: signal.structure?.valid ? [
      { price: signal.structure.fibOrigin,  type: 'fib', label: 'Origin (0)' },
      { price: signal.structure.fibExtreme, type: 'fib', label: 'Extreme (1)' },
    ] : [],
  };
}

// NOTE: the old buildVisionPrompt() (Groq screenshot-vision prompt) was
// removed here. It's no longer used — signals now come from A-Signal.js
// (real math on real Yahoo Finance candles), never from a chart screenshot.
// See jarvisDNA.js → trading.validated_strategy for the logic it replaced.

// Format analysis result as JARVIS chat message
function formatAnalysisAsChat(analysis) {
  if (!analysis) return '';
  const tp    = analysis.trade_plan || {};
  const action = tp.action || 'WAIT';
  const grade  = analysis.grade || 'C';
  const score  = analysis.setup_score || 0;

  let msg = `📡 **LIVE CHART ANALYSIS — ${analysis.symbol || 'XAUUSD'} ${analysis.timeframe || ''}**\n\n`;

  // Signal header
  if (action === 'BUY') {
    msg += `🟢 **SIGNAL: BUY** | Grade **${grade}** | Score: ${score}/100\n\n`;
  } else if (action === 'SELL') {
    msg += `🔴 **SIGNAL: SELL** | Grade **${grade}** | Score: ${score}/100\n\n`;
  } else {
    msg += `⏸ **SIGNAL: WAIT** | Score: ${score}/100\n\n`;
  }

  // Market read
  msg += `📊 **Market Read:** ${analysis.trend || '—'} | Structure: ${analysis.structure || '—'} | Bias: ${analysis.bias || '—'}\n`;
  if (analysis.price) msg += `💰 **Price:** $${analysis.price}\n`;

  // Trade plan
  if (action !== 'WAIT' && tp.entry) {
    msg += `\n📐 **Trade Plan:**\n`;
    msg += `• Entry: **$${tp.entry}**\n`;
    msg += `• Stop Loss: **$${tp.sl}** 🛡️\n`;
    msg += `• TP1: **$${tp.tp1}** 🎯\n`;
    if (tp.tp2) msg += `• TP2: **$${tp.tp2}** 🎯\n`;
    msg += `• R:R = **${tp.rr}** | Lot: ${tp.lot_size} | Risk: $${tp.risk_usd}\n`;
    if (tp.reason) msg += `\n📌 **Reason:** ${tp.reason}\n`;
  } else if (action === 'WAIT' && tp.reason) {
    msg += `\n⏳ **Wait for:** ${tp.reason}\n`;
  }

  // Key observation
  if (analysis.key_observation) {
    msg += `\n💡 **Key Observation:** ${analysis.key_observation}\n`;
  }

  // Rules applied
  if (analysis.rules_applied?.length > 0) {
    msg += `\n✅ **Rules Applied:** ${analysis.rules_applied.join(' | ')}\n`;
  }

  // Alert
  if (analysis.alert) {
    msg += `\n⚠️ **ALERT:** ${analysis.alert}\n`;
  }

  // Liquidity
  if (analysis.liquidity && analysis.liquidity !== 'None visible') {
    msg += `\n🎯 **Liquidity:** ${analysis.liquidity}\n`;
  }

  return msg;
}

const S = {
  wrap:   { padding: '20px', fontFamily: "'Courier New', monospace", color: THEME.colors.text },
  card:   { background: '#0d1520', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '14px', padding: '18px', marginBottom: '16px' },
  title:  { fontSize: '11px', color: THEME.colors.blue, letterSpacing: '2px', marginBottom: '12px', fontWeight: '700' },
  btn:    (c='blue') => ({
    padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
    background: c === 'red' ? 'rgba(239,68,68,0.15)' : c === 'green' ? 'rgba(0,255,136,0.12)' : 'rgba(0,212,255,0.12)',
    border:     c === 'red' ? '1px solid rgba(239,68,68,0.3)' : c === 'green' ? '1px solid rgba(0,255,136,0.25)' : '1px solid rgba(0,212,255,0.25)',
    color:      c === 'red' ? '#f87171' : c === 'green' ? THEME.colors.green : THEME.colors.blue,
  }),
  badge: (v) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
    background: v === 'BULLISH' ? 'rgba(0,255,136,0.15)' : v === 'BEARISH' ? 'rgba(239,68,68,0.15)' : 'rgba(255,215,0,0.1)',
    color:      v === 'BULLISH' ? '#00ff88' : v === 'BEARISH' ? '#f87171' : '#ffd700',
    border:     v === 'BULLISH' ? '1px solid rgba(0,255,136,0.3)' : v === 'BEARISH' ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(255,215,0,0.2)',
  }),
};

export default function LiveMonitor() {
  const { state, dispatch } = useJarvisContext();
  const livePrice = state.price || 0;

  const [monitoring, setMonitoring] = useState(false);
  const [interval,   setIntervalV]  = useState(15);
  const [countdown,  setCountdown]  = useState(0);
  const [analysis,   setAnalysis]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [muted,      setMutedState] = useState(isMuted());
  const [sessionLog, setSessionLog] = useState([]);
  const [listening,  setListening]  = useState(false);
  const [micText,    setMicText]    = useState('');

  const timerRef   = useRef(null);
  const countRef   = useRef(null);
  // Fingerprint of the last spoken result — Start Monitor re-runs
  // analyzeMarket() every scan interval (as often as every 10s), and
  // without this a still-valid signal would get the SAME "trade found"
  // voice alert repeated every single scan for as long as it stays valid.
  // Only speak again when something actually changed.
  const lastAnnouncedRef = useRef(null);
  // Separate dedup ref for the reversal signal (2026-08-23) — kept apart
  // from lastAnnouncedRef above so the trend-signal's and the reversal's
  // "did this change" tracking never collide/overwrite each other; they're
  // genuinely independent signals that can both be live at once.
  const lastReversalAnnouncedRef = useRef(null);
  const isElectron = !!window.electronAPI?.isElectron;
  const [timeframe, setTimeframe] = useState('1h'); // default = Pine-validated; 1m/3m/5m gated (see GATED_TFS)
  const [gateVersion, setGateVersion] = useState(0); // bumped after any backtest so gate badges refresh

  // Live gate status for the 3 short TFs — recomputed whenever a backtest
  // saves a fresh result (gateVersion bump), so 🔒→🔓 updates without reload.
  const shortTfGates = useMemo(() => {
    const out = {};
    GATED_TFS.forEach(tf => { out[tf] = isTimeframeApprovedForLive(tf); });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateVersion]);

  // Load session log
  useEffect(() => { setSessionLog(getSessionLearning()); }, []);

  // ── Shared backtest runner: fetches candles for `tf`, runs the v1 A-Backtest
  // simulation, saves the result + compares to the last saved run. Used by
  // BOTH the manual "🔬 Backtest" button AND the auto-gate check below —
  // one code path, so "Jarvis test करेल आणि निकाल सांगेल" behaves identically
  // whether Sir asks for it or Jarvis triggers it itself.
  const BT_PERIOD = { '1m': '5d', '3m': '5d', '5m': '60d', '15m': '60d', '30m': '60d', '45m': '60d', '1h': '2y', '4h': '2y' };
  const runBacktestFor = useCallback(async (tf) => {
    const period = BT_PERIOD[tf] || '60d';
    const { candles, ok, error: cErr } = await getLiveCandles(tf, period);
    if (!ok || !candles?.length) throw new Error(cErr || 'Candle fetch failed');
    const result = await runBacktest({ candles });
    const comparison = compareToLastBacktest(tf, result);
    saveBacktestResult(tf, result);
    setGateVersion(v => v + 1); // refresh 🔒/🔓 badges
    return { result, comparison };
  }, []);

  // ── Analyze market: REAL data (Yahoo Finance via A-LivePrice), REAL math
  // (A-Structure + A-Fibonacci + A-Regime via A-Signal) — zero screenshots,
  // zero vision-AI guessing. This is what replaced the old screen-capture +
  // Groq vision flow after it was found to be unreliable in practice.
  const analyzeMarket = useCallback(async () => {
    setLoading(true);
    setError('');

    dispatch({ type: A.ADD_MESSAGE, payload: { role: 'user', content: `📡 ${timeframe} — live data + validated logic ने check करतोय...` } });

    try {
      // ── Gate: 1m/3m/5m need a passing backtest before a live signal is
      // given. If this TF has never been tested (or its last result doesn't
      // clear the bar), Jarvis runs the backtest ITSELF right now, reports
      // the result in chat, then re-checks — no manual button needed.
      if (GATED_TFS.includes(timeframe)) {
        let gate = isTimeframeApprovedForLive(timeframe);
        if (!gate.approved) {
          dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: `🔬 ${timeframe} अजून unlock नाही — Jarvis आधी स्वतः backtest चालवतोय...` } });
          const { result: btRes, comparison } = await runBacktestFor(timeframe);
          setBtResult({ ...btRes, comparison });
          gate = isTimeframeApprovedForLive(timeframe); // re-check with the fresh saved result
          const btMsg = btRes.ok
            ? `📊 **${timeframe} Auto-Backtest (v1):** ${btRes.nTrades} trades, Win Rate ${btRes.winRate ?? '—'}%, Expectancy ${btRes.expectancy ?? '—'}R\n\n${btRes.verdictText}\n\n${btRes.note}`
            : `❌ ${timeframe} backtest error: ${btRes.error}`;
          dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: btMsg } });
        }

        if (!gate.approved) {
          setAnalysis(null);
          const waitMsg = `⏸ **${timeframe} — Live signal अजून बंद.**\n\n${gate.reason}\n\n15m/30m/45m/1h/4h आधीच Pine वर validated आहेत, ते वापरा — किंवा ${timeframe} चा backtest history कालांतराने सुधारला की आपोआप unlock होईल.`;
          dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: waitMsg } });
          speak(`${timeframe} अजून live trading साठी unlock नाही, Sir. ${gate.reason}`);
          setLoading(false);
          return;
        }

        dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: `🔓 ${gate.reason} — आता live signal काढतोय.` } });
      }

      const { candles, ok: cOk, error: cErr } = await getLiveCandles(timeframe, '5d');
      if (!cOk || !candles?.length) throw new Error(cErr || 'Candle fetch failed');

      const htfTF = HTF_MAP[timeframe] || '4h';
      const { candles: htfCandles, ok: htfOk } = await getLiveCandles(htfTF, '3mo');
      const htfBias = htfOk ? calcHtfBias(htfCandles) : null;

      const signal = await runSignal({ candles, htfBias });
      if (!signal.ok) throw new Error(signal.error || 'Signal calculation failed');

      let riskResult = null;
      if (signal.action !== 'WAIT') {
        const risk = getRiskSettings() || {};
        riskResult = await runRisk({
          account_size: risk.account_balance,
          risk_pct:     risk.risk_per_trade_percent,
          entry: signal.entry, sl: signal.sl, tp: signal.target,
        });
      }

      const parsed = adaptSignalToLegacyShape(signal, timeframe, riskResult);
      setAnalysis(parsed);

      // Feed real Structure/OTE-zone/HTF/ADX into ICT Module + Confluence
      // Engine — same path the chat's multi-timeframe check uses.
      try { autoLogStructureToICT(timeframe, signal); } catch { /* non-fatal */ }
      try { saveSignalSnapshot(timeframe, signal); } catch { /* non-fatal */ }
      try { await autoLogOrderBlocksToICT(timeframe, candles, signal); } catch { /* non-fatal */ }

      // Auto-log every firm signal as a demo trade in the Journal — same
      // path the chat's multi-timeframe check uses, so a signal found via
      // "Check Now" gets forward-tracked too, not just chat-triggered ones.
      if (signal.action !== 'WAIT') {
        try { autoLogSignalAsTrade(timeframe, signal); } catch { /* non-fatal */ }
      }

      autoLearnFromAnalysis(parsed, 'Live Monitor');

      const chatContent = formatAnalysisAsChat(parsed);
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: chatContent } });
      dispatch({ type: A.SET_SCREEN, payload: 'v5chat' });

      saveSessionLearning({
        type:    'observation',
        title:   `${parsed.symbol} ${parsed.timeframe} — ${parsed.trend} | Score: ${parsed.setup_score}`,
        content: parsed.key_observation || '',
        price:   parsed.price,
      });
      setSessionLog(getSessionLearning());

      if (parsed.trade_plan?.action !== 'WAIT') {
        const tp = parsed.trade_plan;
        // Fingerprint by the actual trade specifics, not just "a signal
        // exists" — a new entry/SL on the same timeframe+direction still
        // counts as worth re-announcing, but the identical still-valid
        // setup on the next scan tick does not.
        const fp = `TRADE|${timeframe}|${tp.action}|${tp.entry}|${tp.sl}`;
        if (lastAnnouncedRef.current !== fp) {
          lastAnnouncedRef.current = fp;
          speak(`Hello NK, trade found! ${timeframe} chart var ${tp.action} setup, Grade ${parsed.grade}. Entry ${tp.entry}, stop loss ${tp.sl}, target ${tp.tp1}. Please check fast, Sir.`, { priority: true });
        }
      } else {
        // WAIT-state re-announcements are muted the same way — otherwise
        // a continuous monitor would repeat "still waiting" every scan.
        const fp = `WAIT|${timeframe}|${parsed.trend}`;
        if (lastAnnouncedRef.current !== fp) {
          lastAnnouncedRef.current = fp;
          speak(`${parsed.symbol} ${timeframe} — ${parsed.trend} bias. Wait. ${parsed.key_observation}`);
        }
      }

      // Reversal check — SEPARATE from the trend signal above, 4h ONLY
      // (Sir's ask, 2026-08-23: "ani Jarvis ai madhe" — port the Pine/
      // Telegram-relay reversal concept into the app too). Reuses the
      // SAME `candles` already fetched for this scan — no extra API call.
      // The 5-TF backtest proved this concept only has a real edge on 4h,
      // so this deliberately never runs for any other timeframe, matching
      // the hard lock already enforced in the Pine indicator and relay.
      if (timeframe === '4h') {
        try {
          const reversal = await runReversal({ candles });
          if (reversal.ok && reversal.action !== 'WAIT') {
            try { autoLogReversalAsTrade(timeframe, reversal); } catch { /* non-fatal */ }
            const rfp = `REV|${reversal.action}|${reversal.entry}|${reversal.sl}`;
            if (lastReversalAnnouncedRef.current !== rfp) {
              lastReversalAnnouncedRef.current = rfp;
              speak(`Hello NK, reversal found! 4h chart var ${reversal.action} reversal setup. Entry ${reversal.entry}, stop loss ${reversal.sl}, target ${reversal.target}. Please check fast, Sir.`, { priority: true });
            }
          }
        } catch { /* non-fatal — reversal check must never block the main trend signal above */ }
      }

      if (isElectron) window.electronAPI.sendAnalysis(parsed);
    } catch (err) {
      setError(err.message);
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: `❌ Signal error: ${err.message}` } });
    }
    setLoading(false);
  }, [isElectron, timeframe, dispatch, runBacktestFor]);

  // Manual check
  const handleCapture = useCallback(async () => { await analyzeMarket(); }, [analyzeMarket]);

  // ── Backtest button: fast in-app first-pass filter (v1 simplified, see
  // A_Backtest.js note). Not a substitute for the Pine backtest, but lets
  // us instantly sanity-check any timeframe before spending time on
  // TradingView. Reuses runBacktestFor() — same path the auto-gate uses.
  const [btResult, setBtResult] = useState(null);
  const [btLoading, setBtLoading] = useState(false);

  const handleBacktest = useCallback(async () => {
    setBtLoading(true);
    setBtResult(null);
    try {
      const { result, comparison } = await runBacktestFor(timeframe);
      setBtResult({ ...result, comparison });

      let msg = result.ok
        ? `📊 **${timeframe} Backtest (v1, simplified):** ${result.nTrades} trades, Win Rate ${result.winRate ?? '—'}%, Expectancy ${result.expectancy ?? '—'}R\n\n${result.verdictText}\n\n${result.note}`
        : `❌ Backtest error: ${result.error}`;
      if (comparison?.warning) {
        msg += `\n\n${comparison.warning}`;
        speakAlert(comparison.warning);
      } else if (comparison?.status === 'IMPROVED') {
        msg += `\n\n📈 मागच्या check (${comparison.prevDate.slice(0,10)}) पेक्षा सुधारलं — Expectancy +${comparison.delta}R.`;
      }
      if (GATED_TFS.includes(timeframe) && result.ok) {
        const gate = isTimeframeApprovedForLive(timeframe);
        msg += gate.approved ? `\n\n🔓 ${gate.reason}` : `\n\n🔒 ${gate.reason}`;
      }
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: msg } });
    } catch (err) {
      setBtResult({ ok: false, error: err.message });
    }
    setBtLoading(false);
  }, [timeframe, dispatch, runBacktestFor]);

  // ── "Study ADX" — Sir noticed by eye that 1m looks better when ADX
  // visually clears ~30-32. This automates checking that PROPERLY: runs the
  // same walk-forward backtest at ADX Threshold 20/25/30/32/35/40/45/50 and
  // shows which ones actually clear a real trade count with a real edge —
  // instead of trusting a 5-trade sample. Only meaningful for the gated
  // short TFs (1m/3m/5m); 15m+ already has a Pine-confirmed threshold (20).
  const [studyResult, setStudyResult] = useState(null);
  const [studyLoading, setStudyLoading] = useState(false);

  const handleStudyADX = useCallback(async () => {
    setStudyLoading(true);
    setStudyResult(null);
    dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: `🔬🔍 ${timeframe} — वेगवेगळे ADX Threshold (20 ते 50) आजमावतोय, थोडा वेळ लागेल...` } });
    try {
      const period = BT_PERIOD[timeframe] || '60d';
      const { candles, ok, error: cErr } = await getLiveCandles(timeframe, period);
      if (!ok || !candles?.length) throw new Error(cErr || 'Candle fetch failed');

      const study = await sweepADXThreshold({ candles });
      setStudyResult(study);

      const table = study.rows.map(r =>
        `Threshold ${r.threshold}: ${r.nTrades} trades${r.enoughTrades ? '' : ' (कमी)'}, Expectancy ${r.expectancy ?? '—'}R`
      ).join('\n');
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: `📋 **${timeframe} ADX Study:**\n\n${table}\n\n${study.note}` } });
    } catch (err) {
      setStudyResult({ ok: false, error: err.message });
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: `❌ ADX study error: ${err.message}` } });
    }
    setStudyLoading(false);
  }, [timeframe, dispatch]);

  // Start/stop auto monitoring
  const startMonitor = useCallback(() => {
    setMonitoring(true);
    setCountdown(interval);
    lastAnnouncedRef.current = null; // fresh session — always announce the first finding
    speak(`Live monitor started. ${timeframe} chart, scanning every ${interval} seconds.`);

    let cnt = interval;
    countRef.current = setInterval(() => {
      cnt--;
      setCountdown(cnt);
      if (cnt <= 0) { cnt = interval; setCountdown(interval); }
    }, 1000);

    timerRef.current = setInterval(analyzeMarket, interval * 1000);
    setTimeout(analyzeMarket, 500); // first check immediately
  }, [interval, timeframe, analyzeMarket]);

  const stopMonitor = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(countRef.current);
    setMonitoring(false);
    setCountdown(0);
    speak('Live monitor paused.');
  }, []);

  useEffect(() => () => { clearInterval(timerRef.current); clearInterval(countRef.current); }, []);

  // Mic voice input — uses knowledge base + live price
  const handleMic = () => {
    if (listening) { stopListening(); setListening(false); return; }
    const ok = startListening((text) => {
      setListening(false);
      setMicText(text);
      if (detectSaveCommand(text)) {
        handleSaveRule(text);
      } else {
        // Push user voice to chat
        dispatch({ type: A.ADD_MESSAGE, payload: { role: 'user', content: `🎤 ${text}` } });

        const chunks = getKnowledgeChunks().slice(0, 10).map(c => `[${c.category}] ${c.title}: ${c.content.slice(0, 100)}`).join('\n');
        const rules  = (getRules() || []).filter(r => r.status === 'active').slice(0, 5).map(r => `${r.title}: ${r.action}`).join('\n');
        const ctxSys = `You are JARVIS — elite XAU/USD trading AI and Sir's trading partner.
Live XAU/USD price: $${livePrice || 'unknown'}
${analysis ? `Last chart analysis: ${analysis.bias} bias | Score ${analysis.setup_score} | ${analysis.key_observation}` : ''}

Sir's Knowledge Base:\n${chunks}
Sir's Rules:\n${rules}

Be decisive. Give exact prices. Max 4 lines. End with BUY/SELL/WAIT + price.`;

        fetch(apiUrl('/api/chat'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            system:   ctxSys,
            messages: [{ role: 'user', content: text }],
            max_tokens: 600, // 300 truncated groq/compound-mini mid-sentence on Marathi answers
          }),
        }).then(r => r.json()).then(d => {
          const reply = d.content?.[0]?.text || '';
          if (reply) {
            speak(reply);
            dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: reply } });
          }
        });
      }
      setTimeout(() => setMicText(''), 3000);
    });
    if (ok) setListening(true);
  };

  // Save logic/rule/observation to JARVIS memory (Knowledge Base + Rule Book)
  const handleSaveRule = (customText = '') => {
    const isFromAnalysis = !!analysis;
    const content = isFromAnalysis
      ? `${analysis.key_observation || ''} | Bias: ${analysis.bias} | Structure: ${analysis.structure} | Score: ${analysis.setup_score} | Rules: ${(analysis.rules_applied || []).join(', ')}`
      : customText;

    const title = isFromAnalysis
      ? `${analysis.symbol || 'XAUUSD'} ${analysis.timeframe || 'H1'} — ${analysis.bias || 'Observation'} (Score ${analysis.setup_score || 0})`
      : `Voice Note: ${customText.slice(0, 60)}`;

    // 1. Save to Knowledge Base as a chunk (persists for future analysis)
    saveKnowledgeChunk({
      source:   'JARVIS Live Monitor',
      category: 'rule',
      title:    title,
      content:  content,
      tags:     ['live', 'screen-monitor', (analysis?.trend || 'chart').toLowerCase(), (analysis?.bias || 'observe').toLowerCase()],
    });

    // 2. Also save to Rule Book if there's an analysis with a clear signal
    if (isFromAnalysis && analysis.trade_plan?.action && analysis.trade_plan.action !== 'WAIT') {
      saveRule({
        seed_id:       `live_${Date.now()}`,
        title:         title,
        condition:     `${analysis.structure || ''} + ${analysis.candle_pattern || ''} + ${analysis.trend || ''} trend`,
        action:        analysis.trade_plan.action,
        source:        'JARVIS Live Monitor — Voice Save',
        auto_extracted: false,
        notes:         content.slice(0, 200),
      });
    }

    // 3. Save to session log
    saveSessionLearning({ type: 'rule', title, content, saved_as_rule: true });
    setSessionLog(getSessionLearning());

    speak('Saved to JARVIS memory, Sir. Knowledge base updated.');
    dispatch({ type: A.ADD_MESSAGE, payload: {
      role: 'assistant',
      content: `✅ **Saved to JARVIS Memory:**\n\n📚 **Knowledge Base** — "${title}"\n\n${content.slice(0, 200)}\n\nThis will be used in all future analyses.`,
    }});
  };

  const handleMarkLevel = (id, result) => {
    markSessionLevelResult(id, result);
    setSessionLog(getSessionLearning());
    const msg = result === 'held' ? 'Level held — noted Sir.' : 'Level rejected — learning updated Sir.';
    speak(msg);
  };

  const handleToggleMute = () => { const m = toggleMute(); setMutedState(m); };
  const handleOpenOverlay = () => { if (isElectron) window.electronAPI.openOverlay(); };

  // Wipe everything built from the old screenshot+Groq-vision analysis —
  // knowledge chunks, rules auto-saved from it, trades/predictions/session
  // log, and the raw vision-history key. Does NOT touch risk settings,
  // daily teaching log, or general assistant memory — see
  // storage.js → clearLegacyTradingMemory() comment for exactly what's kept.
  const handleClearMemory = () => {
    if (!window.confirm('जुनी trading memory (जुन्या screenshot-analysis वरून साठलेली knowledge/rules/predictions/session log) पूर्ण पुसायची? हे परत मिळणार नाही.')) return;
    clearLegacyTradingMemory();
    MemoryManager.clearNS(NS.TRADING);
    setSessionLog([]);
    setAnalysis(null);
    speak('जुनी trading memory पुसली, Sir. आता फक्त validated logic पासूनच नवीन data साठवला जाईल.');
    dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: '🗑️ **जुनी trading memory पुसली.** Knowledge, Rules, Trades, Predictions, Session log — सगळं रिकामं. आता पुढचा प्रत्येक record फक्त validated Structure+Fib+ADX logic कडून येईल.' } });
  };

  return (
    <div style={S.wrap}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '16px', fontWeight: '800', color: THEME.colors.blue, letterSpacing: '2px' }}>
            📡 JARVIS LIVE MONITOR
          </div>
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>TradingView Screen Scanner</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleToggleMute} style={S.btn(muted ? 'red' : 'blue')} title="Mute/Unmute">
            {muted ? '🔇' : '🔊'}
          </button>
          {isElectron && (
            <button onClick={handleOpenOverlay} style={S.btn('green')}>
              🪟 Overlay
            </button>
          )}
          <button onClick={handleClearMemory} style={S.btn('red')} title="जुनी screenshot-based trading memory पुसा">
            🗑️ Clear Legacy Memory
          </button>
        </div>
      </div>

      {/* Timeframe select — replaces the old Screen Source picker. Data
          comes from Yahoo Finance (A-LivePrice), not a screenshot, so this
          works identically in-browser or in Electron. */}
      <div style={S.card}>
        <div style={S.title}>TIMEFRAME (Pine-validated)</div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['15m', '30m', '45m', '1h', '4h'].map(tf => (
            <button key={tf} onClick={() => setTimeframe(tf)}
              style={{ ...S.btn(timeframe === tf ? 'green' : 'blue'), padding: '6px 14px' }}>
              {tf}
            </button>
          ))}
        </div>

        {/* 1m/3m/5m — GATED, not removed. Pine backtests were weak/negative
            (3m: -0.35R, 5m: +0.06R barely breakeven, 1m untested), so these
            start locked. Selecting one and hitting "Check Now" makes Jarvis
            run ITS OWN backtest first; it only gives a live signal if that
            (or a later, better) result clears the bar. Unlocks automatically
            — no code change needed when the numbers improve. */}
        <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginTop: '10px', marginBottom: '4px' }}>
          SHORT TF (auto backtest-gated — locked पर्यंत खात्रीशीर result मिळत नाही):
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {GATED_TFS.map(tf => {
            const g = shortTfGates[tf];
            return (
              <button key={tf} onClick={() => setTimeframe(tf)}
                title={g?.reason || ''}
                style={{ ...S.btn(timeframe === tf ? 'green' : (g?.approved ? 'blue' : 'red')), padding: '6px 14px', opacity: g?.approved ? 1 : 0.75 }}>
                {g?.approved ? '🔓' : '🔒'} {tf}
              </button>
            );
          })}
        </div>
        {GATED_TFS.includes(timeframe) && (
          <div style={{ fontSize: '10px', color: shortTfGates[timeframe]?.approved ? '#00ff88' : '#f87171', marginTop: '6px' }}>
            {shortTfGates[timeframe]?.approved ? '🔓' : '🔒'} {shortTfGates[timeframe]?.reason}
          </div>
        )}

        <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginTop: '8px' }}>
          HTF auto: {HTF_MAP[timeframe] || '4h'} · Source: Yahoo Finance (no screenshot needed)
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>Scan every:</span>
          {[10, 15, 30, 60].map(v => (
            <button key={v} onClick={() => setIntervalV(v)}
              style={{ ...S.btn(interval === v ? 'green' : 'blue'), padding: '4px 10px' }}>
              {v}s
            </button>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {!monitoring ? (
          <button onClick={startMonitor}
            style={{ ...S.btn('green'), flex: 1, padding: '10px' }}>
            ▶ Start Monitor
          </button>
        ) : (
          <button onClick={stopMonitor} style={{ ...S.btn('red'), flex: 1, padding: '10px' }}>
            ⏸ Pause  (next scan: {countdown}s)
          </button>
        )}
        <button onClick={handleCapture} disabled={loading}
          style={{ ...S.btn('blue'), padding: '10px 16px' }}>
          {loading ? '⏳' : '📊'} Check Now
        </button>
        <button onClick={handleBacktest} disabled={btLoading}
          style={{ ...S.btn('green'), padding: '10px 16px' }}
          title="Fast in-app backtest (v1, simplified — see note in results)">
          {btLoading ? '⏳' : '🔬'} Backtest {timeframe}
        </button>
        {GATED_TFS.includes(timeframe) && (
          <button onClick={handleStudyADX} disabled={studyLoading}
            style={{ ...S.btn('blue'), padding: '10px 16px' }}
            title="ADX Threshold 20-50 सगळे आजमावून बघतो, कुठला खरंच काम करतो">
            {studyLoading ? '⏳' : '🔬🔍'} Study ADX
          </button>
        )}
        <button onClick={handleMic}
          style={{ ...S.btn(listening ? 'red' : 'blue'), padding: '10px 16px' }}>
          {listening ? '🔴 Listening...' : '🎤 Mic'}
        </button>
      </div>

      {micText && (
        <div style={{ ...S.card, background: 'rgba(0,212,255,0.05)', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', color: THEME.colors.blue }}>🎤 You said: </span>
          <span style={{ fontSize: '12px', color: THEME.colors.text }}>"{micText}"</span>
        </div>
      )}

      {btResult && (
        <div style={S.card}>
          <div style={S.title}>🔬 {timeframe} BACKTEST (v1, simplified)</div>
          {btResult.ok ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                {[
                  ['Trades',     btResult.nTrades],
                  ['Win Rate',   btResult.winRate != null ? `${btResult.winRate}%` : '—'],
                  ['Payoff',     btResult.payoff != null ? `${btResult.payoff}:1` : '—'],
                  ['Expectancy', btResult.expectancy != null ? `${btResult.expectancy}R` : '—'],
                  ['Profit Factor', btResult.profitFactor ?? '—'],
                  ['Worst Streak',  `${btResult.worstLossStreak} in a row`],
                ].map(([l, v]) => (
                  <div key={l} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '6px', padding: '6px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '9px', color: THEME.colors.textMuted }}>{l}</div>
                    <div style={{ fontSize: '13px', color: THEME.colors.text, fontWeight: '700' }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: THEME.colors.text, marginBottom: '6px' }}>{btResult.verdictText}</div>
              {btResult.comparison && (
                <div style={{
                  fontSize: '11px', padding: '6px 10px', borderRadius: '6px', marginBottom: '6px',
                  color: btResult.comparison.status === 'DEGRADED' ? '#f87171' : btResult.comparison.status === 'IMPROVED' ? '#00ff88' : THEME.colors.textMuted,
                  background: btResult.comparison.status === 'DEGRADED' ? 'rgba(239,68,68,0.1)' : 'transparent',
                }}>
                  {btResult.comparison.status === 'DEGRADED' ? '📉' : btResult.comparison.status === 'IMPROVED' ? '📈' : '➡️'} मागच्या check ({btResult.comparison.prevDate.slice(0,10)}, Expectancy {btResult.comparison.prevExpectancy}R) पासून: {btResult.comparison.delta > 0 ? '+' : ''}{btResult.comparison.delta}R
                </div>
              )}
              <div style={{ fontSize: '10px', color: '#ffd700', fontStyle: 'italic' }}>⚠️ {btResult.note}</div>
            </>
          ) : (
            <div style={{ color: '#f87171', fontSize: '12px' }}>❌ {btResult.error}</div>
          )}
        </div>
      )}

      {studyResult && (
        <div style={S.card}>
          <div style={S.title}>🔬🔍 {timeframe} ADX THRESHOLD STUDY</div>
          {studyResult.ok ? (
            <>
              <div style={{ marginBottom: '10px' }}>
                {studyResult.rows.map(r => (
                  <div key={r.threshold} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '6px 10px', marginBottom: '4px', borderRadius: '6px',
                    background: studyResult.best?.threshold === r.threshold ? 'rgba(0,255,136,0.12)' : 'rgba(0,0,0,0.25)',
                    border: studyResult.best?.threshold === r.threshold ? '1px solid rgba(0,255,136,0.3)' : '1px solid transparent',
                  }}>
                    <span style={{ fontSize: '12px', color: THEME.colors.text }}>
                      Threshold <b>{r.threshold}</b> {studyResult.best?.threshold === r.threshold && '⭐'}
                    </span>
                    <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>
                      {r.nTrades} trades{!r.enoughTrades && ' (कमी)'} · Expectancy {r.expectancy != null ? `${r.expectancy}R` : '—'}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '11px', color: studyResult.best ? '#00ff88' : '#ffd700' }}>{studyResult.note}</div>
            </>
          ) : (
            <div style={{ color: '#f87171', fontSize: '12px' }}>❌ {studyResult.error}</div>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#f87171' }}>
          ❌ {error}
        </div>
      )}

      {/* Live Analysis Result */}
      {analysis && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={S.title}>📊 LIVE ANALYSIS</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {analysis.grade && (
                <span style={{
                  padding: '2px 10px', borderRadius: '20px', fontSize: '13px', fontWeight: '800',
                  background: analysis.grade.startsWith('A') ? 'rgba(0,255,136,0.15)' : analysis.grade === 'B' ? 'rgba(255,215,0,0.12)' : 'rgba(239,68,68,0.12)',
                  color:      analysis.grade.startsWith('A') ? '#00ff88' : analysis.grade === 'B' ? '#ffd700' : '#f87171',
                  border:     `1px solid ${analysis.grade.startsWith('A') ? 'rgba(0,255,136,0.3)' : analysis.grade === 'B' ? 'rgba(255,215,0,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}>Grade {analysis.grade}</span>
              )}
              <span style={S.badge(analysis.trend)}>{analysis.trend}</span>
            </div>
          </div>

          {/* ── TRADE PLAN — Main Box ─────────────────────────────── */}
          {analysis.trade_plan && (
            <div style={{
              marginBottom: '14px', borderRadius: '10px', overflow: 'hidden',
              border: analysis.trade_plan.action === 'WAIT'
                ? '1px solid rgba(255,215,0,0.3)'
                : analysis.trade_plan.action === 'BUY'
                ? '1px solid rgba(0,255,136,0.35)'
                : '1px solid rgba(239,68,68,0.35)',
            }}>
              {/* Action header */}
              <div style={{
                padding: '10px 14px',
                background: analysis.trade_plan.action === 'WAIT'
                  ? 'rgba(255,215,0,0.1)'
                  : analysis.trade_plan.action === 'BUY'
                  ? 'rgba(0,255,136,0.12)'
                  : 'rgba(239,68,68,0.12)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{
                  fontSize: '18px', fontWeight: '900', letterSpacing: '2px',
                  color: analysis.trade_plan.action === 'WAIT' ? '#ffd700' : analysis.trade_plan.action === 'BUY' ? '#00ff88' : '#f87171',
                }}>
                  {analysis.trade_plan.action === 'BUY' ? '▲ BUY' : analysis.trade_plan.action === 'SELL' ? '▼ SELL' : '⏸ WAIT'}
                </span>
                {analysis.trade_plan.rr && analysis.trade_plan.action !== 'WAIT' && (
                  <span style={{ fontSize: '12px', color: '#ffd700', fontWeight: '700' }}>R:R {analysis.trade_plan.rr}</span>
                )}
              </div>

              {/* Trade levels */}
              {analysis.trade_plan.action !== 'WAIT' && (
                <div style={{ padding: '10px 14px', background: 'rgba(0,0,0,0.25)' }}>
                  {[
                    ['Entry',     analysis.trade_plan.entry,    THEME.colors.blue],
                    ['Stop Loss', analysis.trade_plan.sl,       '#f87171'],
                    ['TP1 (50%)', analysis.trade_plan.tp1,      '#00ff88'],
                    ['TP2 (50%)', analysis.trade_plan.tp2,      '#ffd700'],
                  ].map(([label, val, color]) => val && (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>{label}</span>
                      <span style={{ fontSize: '14px', fontWeight: '800', color }}>${val}</span>
                    </div>
                  ))}
                  {analysis.trade_plan.risk_usd && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                      <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>Risk Amount</span>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#f87171' }}>${analysis.trade_plan.risk_usd}</span>
                    </div>
                  )}
                  {analysis.trade_plan.reason && (
                    <div style={{ marginTop: '8px', fontSize: '11px', color: THEME.colors.textMuted, lineHeight: '1.5', fontStyle: 'italic' }}>
                      📌 {analysis.trade_plan.reason}
                    </div>
                  )}
                </div>
              )}

              {/* WAIT reason */}
              {analysis.trade_plan.action === 'WAIT' && analysis.trade_plan.reason && (
                <div style={{ padding: '10px 14px', fontSize: '12px', color: '#ffd700', lineHeight: '1.5' }}>
                  {analysis.trade_plan.reason}
                </div>
              )}
            </div>
          )}

          {/* Score bar */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>Confluence Score</span>
              <span style={{ fontSize: '13px', fontWeight: '700', color: analysis.setup_score >= 65 ? THEME.colors.green : analysis.setup_score >= 50 ? '#ffd700' : '#f87171' }}>
                {analysis.setup_score}/100
              </span>
            </div>
            <div style={{ height: '5px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px' }}>
              <div style={{ width: `${analysis.setup_score}%`, height: '100%', borderRadius: '3px', background: analysis.setup_score >= 65 ? '#00ff88' : analysis.setup_score >= 50 ? '#ffd700' : '#f87171', transition: 'width 0.5s' }} />
            </div>
          </div>

          {/* Market info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px', marginBottom: '12px' }}>
            {[['Price', `$${analysis.price}`], ['TF', analysis.timeframe], ['Structure', analysis.structure]].map(([l, v]) => (
              <div key={l} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '6px', padding: '5px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: '9px', color: THEME.colors.textMuted }}>{l}</div>
                <div style={{ fontSize: '12px', color: THEME.colors.text, fontWeight: '700' }}>{v || '—'}</div>
              </div>
            ))}
          </div>

          {/* Rules applied */}
          {analysis.rules_applied?.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginBottom: '4px' }}>✅ RULES APPLIED</div>
              {analysis.rules_applied.map((r, i) => (
                <div key={i} style={{ fontSize: '11px', color: '#00ff88', padding: '2px 0' }}>• {r}</div>
              ))}
            </div>
          )}

          {/* Drawn Levels */}
          {analysis.drawn_levels?.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginBottom: '4px' }}>✏️ DRAWN LEVELS</div>
              {analysis.drawn_levels.map((lv, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: '12px', color: lv.type === 'resistance' ? '#f87171' : '#00ff88' }}>━ ${lv.price}</span>
                  <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{lv.type}</span>
                </div>
              ))}
            </div>
          )}

          {/* Key Observation */}
          {analysis.key_observation && (
            <div style={{ background: 'rgba(0,212,255,0.06)', borderRadius: '8px', padding: '10px', marginBottom: '10px', fontSize: '12px', color: THEME.colors.text, lineHeight: '1.6' }}>
              💡 {analysis.key_observation}
            </div>
          )}

          {/* Alert */}
          {analysis.alert && (
            <div style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.25)', borderRadius: '8px', padding: '8px 12px', marginBottom: '10px', fontSize: '12px', color: '#ffd700' }}>
              ⚠️ {analysis.alert}
            </div>
          )}

          <button onClick={() => handleSaveRule()} style={{ ...S.btn('green'), width: '100%', padding: '8px' }}>
            💾 Save as Rule in JARVIS Brain
          </button>
        </div>
      )}

      {/* Session Learning Log */}
      {sessionLog.length > 0 && (
        <div style={S.card}>
          <div style={S.title}>🧠 TODAY'S SESSION LEARNING ({sessionLog.length})</div>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {[...sessionLog].reverse().map((item) => (
              <div key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '8px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: THEME.colors.text, fontWeight: '600' }}>{item.title}</div>
                    {item.content && <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '2px' }}>{item.content}</div>}
                    <div style={{ fontSize: '10px', color: 'rgba(148,163,184,0.5)', marginTop: '2px' }}>{item.time} • {item.type}</div>
                  </div>
                  {item.price && !item.result && (
                    <div style={{ display: 'flex', gap: '4px', marginLeft: '8px', flexShrink: 0 }}>
                      <button onClick={() => handleMarkLevel(item.id, 'held')}
                        style={{ ...S.btn('green'), padding: '3px 8px', fontSize: '10px' }}>Held</button>
                      <button onClick={() => handleMarkLevel(item.id, 'rejected')}
                        style={{ ...S.btn('red'), padding: '3px 8px', fontSize: '10px' }}>Rejected</button>
                    </div>
                  )}
                  {item.result && (
                    <span style={{ fontSize: '11px', color: item.result === 'held' ? '#00ff88' : '#f87171', flexShrink: 0 }}>
                      {item.result === 'held' ? '✅ Held' : '❌ Rejected'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
